import { Component, NgZone, OnInit, ViewChild } from '@angular/core';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { routes } from '@app/consts';
import { SanitiseGridDataService } from '@app/core/services/sanitise-grid-data/sanitise-grid-data.service';
import { TranslateService } from '@ngx-translate/core';
import { ExcelExportEvent, GridComponent, GridDataResult, PageChangeEvent, PageSizeItem } from '@progress/kendo-angular-grid';
import { SortDescriptor, State } from '@progress/kendo-data-query';
import { BehaviorSubject, from, Observable } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { PendingTimeOffReportService } from '../../services/pending-time-off-report.service';

@Component({
    selector: 'app-pending-time-off-details-data-grid',
    templateUrl: './pending-time-off-details-data-grid.component.html',
    styleUrls: ['./pending-time-off-details-data-grid.component.scss']
})
export class PendingTimeOffDetailsDataGridComponent implements OnInit {
    @ViewChild(GridComponent)
    public grid: GridComponent;

    public routes: typeof routes = routes;
    public sort: SortDescriptor[] = [];
    public bindingType = 'array';
    public view: Observable<GridDataResult>;
    public gridDataResult: GridDataResult;
    public selectedPositions: any[] = [];
    public pageSizes: (PageSizeItem | number)[] = [
        5,
        10,
        20,
        {
            text: 'All',
            value: 100000,
        },
    ];
    isLoading: boolean;
    pageSize = 4;
    skip = 0;
    sortString: string = "requestedOn desc";
    filterString: string;
    filterInactive = true;

    public columns: any[] = [
        {field: 'employeeFullName', title: this.translate.instant('PendingTimeOffReport-EmployeeFullName')
            , tableId: 'PendingTimeOffReport-EmployeeFullName', dataType: 'String'},
        {field: 'managerFullName', title: this.translate.instant('PendingTimeOffReport-ManagerFullName')
            , tableId: 'PendingTimeOffReport-ManagerFullName', dataType: 'String'},
        {field: 'timeOffRequestStartDate', title: this.translate.instant('PendingTimeOffReport-TimeOffRequestStartDate')
            , tableId: 'PendingTimeOffReport-TimeOffRequestStartDate', dataType: 'String'},
        {field: 'timeOffRequestEndDate', title: this.translate.instant('PendingTimeOffReport-TimeOffRequestEndDate')
            , tableId: 'PendingTimeOffReport-TimeOffRequestEndDate', dataType: 'String'},
        {field: 'hoursInTimeOffRequest', title: this.translate.instant('PendingTimeOffReport-HoursInTimeOffRequest')
            , tableId: 'PendingTimeOffReport-HoursInTimeOffRequest', dataType: 'decimal'},
        {field: 'dateSubmittedOn', title: this.translate.instant('PendingTimeOffReport-DateSubmittedOn')
            , tableId: 'PendingTimeOffReport-DateSubmittedOn', dataType: 'String'},
        {field: 'employeeStatus', title: this.translate.instant('PendingTimeOffReport-EmployeeStatus')
            , tableId: 'PendingTimeOffReport-EmployeeStatus', dataType: 'String'},
        {field: 'requestStatus', title: this.translate.instant('PendingTimeOffReport-RequestStatus')
            , tableId: 'PendingTimeOffReport-RequestStatus', dataType: 'String'},
        {field: 'timeOffType', title: this.translate.instant('PendingTimeOffReport-TimeOffType')
            , tableId: 'PendingTimeOffReport-TimeOffType', dataType: 'String'},
        ];

    public state: State = {
        skip: 0,
        take: 50,
    };
    public query: Observable<GridDataResult>;
    private stateChange = new BehaviorSubject<State>(this.state);
    public allData = (): Observable<GridDataResult> => {
        return from(this.pendingTimeOffReportService.queryAll(this.state, this.filterString, this.sortString, this.filterInactive));
    };

    public filterCategories: any[] = [
        {field: 'RequestedBy.FirstName', title: this.translate.instant('PendingTimeOffReport-EmployeeFirstName'), dataType: "String"},
        {field: 'RequestedBy.LastName', title: this.translate.instant('PendingTimeOffReport-EmployeeLastName'), dataType: "String"},
        {field: 'RequestedOn', title: this.translate.instant('PendingTimeOffReport-DateSubmittedOn'), dataType: "Date"},

        {field: "RequestedBy.Status.Id", title: this.translate.instant('PendingTimeOffReport-EmployeeStatus'), dataType: "ProvidedList", listOptions: {
                list: [{id: true, name: 'Active'}, {id: false, name: 'Inactive'},],
                id: 'id',
                name: 'name'
        }}
    ]

    constructor(
        private translate: TranslateService,
        private ngZone: NgZone,
        private pendingTimeOffReportService: PendingTimeOffReportService,
        private sanitiseGridDataService: SanitiseGridDataService
    ) {

        this.query = this.stateChange.pipe(
            tap((state) => {
                this.state = state;
                this.isLoading = true;
            }),
            switchMap((state) => pendingTimeOffReportService.fetch(state, this.filterString, this.sortString, false,
                this.filterInactive)),
            tap(() => {
                this.isLoading = false;
            })
        );

        // Bind 'this' explicitly to capture the execution context of the component.
        this.allData = this.allData.bind(this);

    }

    ngOnInit(): void {
    }

    public pageChange(state: PageChangeEvent): void {
        this.stateChange.next(state);
    }

    public onExcelExport(e: ExcelExportEvent): void {
        e = this.sanitiseGridDataService.sanitise(e);
    }

    toggleFilterInactive($event: MatSlideToggleChange): void {
        this.filterInactive = $event.checked;
        this.stateChange.next(this.state);
    }

    filterCallback(filterString: string) {
        this.filterString = filterString;
        this.state.skip = 0;
        this.state.take = 50;
        this.stateChange.next(this.state);
    }
}
